import { Button, Flex, Group, Modal, Stack, Text } from '@mantine/core';
/* eslint-disable max-statements */
import { cloneDeep, get } from 'lodash';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Declaration, DeclarationDocumentsEnum, declarationDocumentsIdsMapping, Documents } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { MutateDeclarationDocumentOperation } from '@hooks/declaration';
import { Table, TableActionEnum, TableActions, TableColumnProps } from '../';
import { DeclarationDetailsDocDetailsProps, DeclarationDocumentView, isDeclarationDocumentUnique } from './DeclarationDocumentView';

export const documentsLabels = {
  [DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE]: 'Additional fiscal reference',
  [DeclarationDocumentsEnum.TRANSPORT_DOCUMENTS]: 'Transport document',
  [DeclarationDocumentsEnum.AUTHORISATION_DOCUMENTS]: 'Authorisation document',
  [DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS]: 'Additions and deductions',
  [DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS]: 'Previous document',
  [DeclarationDocumentsEnum.SUPPORTING_DOCUMENTS]: 'Supporting document',
  [DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS]: 'Guarantee document',
  [DeclarationDocumentsEnum.ADDITIONAL_REFERENCE]: 'Additional reference',
  [DeclarationDocumentsEnum.ADDITIONAL_INFORMATION]: 'Additional information',
  [DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT]: 'Warehouse document',
};

export interface DocumentItem {
  type: DeclarationDocumentsEnum;
  label?: string;
  amount?: number;
  code: string;
  referenceNumber?: string;
  dateOfValidity?: string;
  dataSource: any;
}

export interface DocumentChangeProps {
  operation: MutateDeclarationDocumentOperation;
  type: DeclarationDocumentsEnum;
  id: Record<string, string>;
  data?: any;
}

interface DeclarationDocumentListProps {
  editDisabled: boolean;
  onDocumentChange(document: DocumentChangeProps): void;
  documents: Documents;
  includeDocumentTypes?: DeclarationDocumentsEnum[];
  excludeDocumentTypes?: DeclarationDocumentsEnum[];
  defaultHeaderValue?: boolean;
  headerDisabled?: boolean;
  isHighValue: boolean;
  declaration?: Declaration;
  isGoodsItem?: boolean;
  'data-pw-prefix'?: string;
}

export const DeclarationDocumentList: React.FC<DeclarationDocumentListProps> = ({
  documents,
  includeDocumentTypes,
  excludeDocumentTypes,
  onDocumentChange,
  editDisabled,
  defaultHeaderValue,
  headerDisabled,
  isHighValue,
  declaration,
  isGoodsItem,
}) => {
  const [data, setData] = useState<DocumentItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDeleteDocument, setSelectedDeleteDocument] = useState<DocumentItem>();
  const [selectedDocument, setSelectedDocument] = useState<DeclarationDetailsDocDetailsProps>(undefined);

  const toggleShowDocumentModal = (inEdit?: boolean) => {
    if (!inEdit) {
      setSelectedDocument(undefined);
    }
    setShowModal(!showModal);
  };

  useEffect(() => {
    const tableData: DocumentItem[] = [];
    for (const [documentType, docs] of Object.entries(documents as any)) {
      if (
        Array.isArray(docs) &&
        (includeDocumentTypes?.includes(documentType as DeclarationDocumentsEnum) ||
          (excludeDocumentTypes && !excludeDocumentTypes.includes(documentType as DeclarationDocumentsEnum)) ||
          (!includeDocumentTypes && !excludeDocumentTypes))
      ) {
        docs.forEach((item) =>
          tableData.push({
            type: documentType as DeclarationDocumentsEnum,
            label: documentsLabels[documentType],
            amount: item.amount,
            code: item.type || item.code || item.role || '',
            referenceNumber:
              item.referenceNumber ||
              item.text ||
              item.vatIdentificationNumber ||
              item.reference?.customsOfficeGuarantee?.referenceNumber ||
              item.identifier ||
              '',
            dateOfValidity: item.dateOfValidity,
            dataSource: item,
          }),
        );
      }
    }
    setData(tableData);
  }, [documents]);

  const handleSavingDocument = async (documentData: { id: Record<string, any>; type: DeclarationDocumentsEnum; data: any }) => {
    const updatedDoc = cloneDeep(documentData.data);
    const { type, id } = documentData;

    if (documents) {
      if (!selectedDocument && !isDeclarationDocumentUnique(documentData.data, type, documents[type])) {
        toast.error('The document already exists!');
        return;
      }

      onDocumentChange({
        operation: selectedDocument ? MutateDeclarationDocumentOperation.UPDATE : MutateDeclarationDocumentOperation.CREATE,
        type,
        id,
        data: updatedDoc,
      });
    }
  };

  const handleTableAction = (type: string, row: DocumentItem) => {
    switch (type) {
      case TableActionEnum.Delete:
        setSelectedDeleteDocument(row);
        break;
      default:
        setSelectedDocument({
          type: row.type,
          dataSource: row.dataSource,
        });
        toggleShowDocumentModal(true);
    }
  };

  const columns: TableColumnProps<DocumentItem>[] = [
    {
      name: 'Type of document',
      cell: (row, rowIdx) => <span data-pw={`cell-type-of-document-${rowIdx}`}>{row.label}</span>,
    },
    {
      name: 'Code',
      cell: (row, rowIdx) => <span data-pw={`cell-code-${rowIdx}`}>{row.code}</span>,
    },
    ...(!isGoodsItem
      ? [
          {
            name: 'Amount',
            cell: (row) => row.amount,
          },
        ]
      : []),

    {
      name: 'Reference',
      cell: (row, rowIdx) => <span data-pw={`cell-reference-${rowIdx}`}>{row.referenceNumber}</span>,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: DocumentItem, rowIdx: number) => (
        <TableActions
          data-pw={rowIdx}
          actions={[
            {
              icon: IconFactory.view,
              text: 'View details',
              onClick: () => handleTableAction(TableActionEnum.Edit, row),
            },
            {
              if: !editDisabled,
              icon: IconFactory.delete,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row),
            },
          ]}
        />
      ),
    },
  ];

  const handleDocumentDelete = (row: DocumentItem) => {
    onDocumentChange({
      operation: MutateDeclarationDocumentOperation.DELETE,
      type: row.type,
      id: declarationDocumentsIdsMapping[row.type].reduce((idsList, id) => {
        return { ...idsList, [id]: get(row.dataSource, id) };
      }, {}),
      data: row.dataSource,
    });
    setSelectedDeleteDocument(undefined);
  };

  return (
    <Stack style={{ flexShrink: 0 }}>
      <Flex justify={'end'} py={12}>
        {!editDisabled && (
          <Button onClick={() => toggleShowDocumentModal()} variant="outline" size="lg">
            Add new
          </Button>
        )}
      </Flex>
      <Table columns={columns} dataSource={data || []} pagination verticalSpacing="xs" />
      {selectedDeleteDocument && (
        <Modal title="Delete document" opened={true} onClose={() => setSelectedDeleteDocument(undefined)} centered zIndex={9999}>
          <Text>Are you sure you want to delete this document?</Text>

          <Group justify="center" mt="xl">
            <Button variant="outline" onClick={() => setSelectedDeleteDocument(undefined)}>
              Cancel
            </Button>
            <Button data-pw="delete-document-btn" variant="filled" color="red" onClick={() => handleDocumentDelete(selectedDeleteDocument)}>
              Delete
            </Button>
          </Group>
        </Modal>
      )}
      {showModal && (
        <DeclarationDocumentView
          opened={true}
          onHide={toggleShowDocumentModal}
          docDetails={selectedDocument}
          includeDocumentTypes={includeDocumentTypes}
          excludeDocumentTypes={excludeDocumentTypes}
          onSave={handleSavingDocument}
          editDisabled={editDisabled}
          defaultHeaderValue={defaultHeaderValue}
          headerDisabled={headerDisabled}
          isHighValue={isHighValue}
          declaration={declaration}
          isGoodsItem={isGoodsItem}
        />
      )}
    </Stack>
  );
};
